import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { api, useMailboxes } from '../../lib/api';
import { useRole } from '../../lib/role';

export default function Mailboxes() {
  const { id = '' } = useParams();
  const { data: rows } = useMailboxes(id);
  const { isAdmin } = useRole();
  const [filter, setFilter] = useState<'all' | 'oauth_error' | 'oauth_done' | 'not_flipped'>('all');
  const [q, setQ] = useState('');
  const [busy, setBusy] = useState<Record<string, boolean>>({});

  const filtered = (rows || []).filter(m => {
    if (isAdmin) {
      if (filter === 'oauth_error' && !m.oauth_error) return false;
      if (filter === 'oauth_done' && !m.oauth_done) return false;
      if (filter === 'not_flipped' && m.upn_flipped) return false;
    }
    if (q && !m.email.toLowerCase().includes(q.toLowerCase())) return false;
    return true;
  });

  async function reconnect(email: string) {
    setBusy(b => ({ ...b, [email]: true }));
    try { await api.reoauthOne(id, email); }
    catch (e: any) { alert(e.message); }
    finally { setBusy(b => ({ ...b, [email]: false })); }
  }

  // ── Operator view: minimal table (email + Reconnect button only). No
  // filter chips, no internal status columns, no admin bulk actions.
  if (!isAdmin) {
    return (
      <div className="p-6 max-w-3xl">
        <div className="flex items-center gap-2 mb-3">
          <input
            className="input max-w-xs text-sm"
            placeholder="search email"
            value={q}
            onChange={e => setQ(e.target.value)}
          />
          <span className="text-xs text-dim">{filtered.length} / {rows?.length ?? 0}</span>
        </div>
        <div className="card overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-bg sticky top-0">
              <tr className="text-left text-[10px] uppercase tracking-wider text-dim">
                <th className="p-3">Email</th>
                <th className="p-3 w-32 text-right"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(m => (
                <tr key={m.email} className="border-t border-line/40">
                  <td className="p-3 font-mono text-xs">{m.email}</td>
                  <td className="p-3 text-right whitespace-nowrap">
                    <button
                      onClick={() => reconnect(m.email)}
                      disabled={!!busy[m.email]}
                      className="btn-primary text-xs disabled:opacity-50"
                    >
                      {busy[m.email] ? 'Reconnecting…' : 'Reconnect'}
                    </button>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && (
                <tr><td colSpan={2} className="p-6 text-center text-dim text-xs">No mailboxes yet.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ── Admin view: full debugging surface. ─────────────────────────────
  return (
    <div className="p-6">
      <div className="flex items-center gap-2 mb-3">
        <input className="input max-w-xs" placeholder="search email" value={q} onChange={e => setQ(e.target.value)} />
        {(['all', 'oauth_done', 'oauth_error', 'not_flipped'] as const).map(k => (
          <button key={k} onClick={() => setFilter(k)}
            className={filter === k ? 'btn-primary text-xs' : 'btn text-xs'}>{k}</button>
        ))}
        <span className="text-xs text-dim">{filtered.length} / {rows?.length ?? 0}</span>
        <div className="ml-auto flex gap-2">
          <button onClick={() => api.resetFailedPasswords(id)} className="btn text-xs">Reset failed passwords</button>
          <button onClick={() => api.reoauthFailed(id)} className="btn text-xs">Re-OAuth failed</button>
          <a href={`/api/tenants/${id}/mailboxes/export.csv`} className="btn-primary text-xs">Export CSV</a>
        </div>
      </div>
      <div className="card overflow-auto">
        <table className="w-full text-xs">
          <thead className="bg-bg sticky top-0">
            <tr className="text-left text-dim">
              <th className="p-2">Email</th>
              <th>Sign-in</th>
              <th>Created</th>
              <th>Pw</th>
              <th>Flipped</th>
              <th>OAuth</th>
              <th>Error</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(m => (
              <tr key={m.email} className="border-t border-line/50">
                <td className="p-2">{m.email}</td>
                <td className="text-dim">{m.upn || '—'}</td>
                <td>{m.created ? '✓' : '—'}</td>
                <td>{m.password_set ? '✓' : '—'}</td>
                <td>{m.upn_flipped ? '✓' : '—'}</td>
                <td>{m.oauth_done ? <span className="text-ok">✓</span> : <span className="text-dim">—</span>}</td>
                <td className="text-err max-w-xs truncate">{m.oauth_error || ''}</td>
                <td className="p-2 text-right whitespace-nowrap">
                  <button onClick={() => api.resetOnePassword(id, m.email)} className="btn text-xs mr-1">reset pw</button>
                  <button onClick={() => api.reoauthOne(id, m.email)} className="btn text-xs">re-oauth</button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
